import React from "react";
import { toast } from "react-toastify";
import {
  Badge,
  Box,
  Button,
  Collapse,
  Divider,
  TextField,
  Typography,
} from "@mui/material";
import { Add, FiberManualRecord, FeedbackOutlined } from "@mui/icons-material";

import { useTaskDetailContext } from "contexts/TaskDetailContext";

import InnerShadowBox from "components/Shared/InnerShadowBox";
import EmptyResult from "components/Shared/Lottie/EmptyResult";

import { useAddResponseToTaskMutation } from "store/services/taskApi";

import { localizeDateTimeMed } from "utils/dateFormats";

const TaskResponses = () => {
  const [showCreateResponse, setShowCreateResponse] = React.useState(false);
  const [newResponse, setNewResponse] = React.useState("");

  const { taskDetail } = useTaskDetailContext();

  const responses = React.useMemo(
    () => taskDetail?.responses ?? [],
    [taskDetail]
  );

  // RTK Query
  const [addNoteToTask, { isLoading: addingResponse }] =
    useAddResponseToTaskMutation();

  // methods
  const handleCancelResponse = () => {
    setShowCreateResponse(false);
    setNewResponse("");
  };

  const handleAddResponse = () => {
    const payload = {
      _id: taskDetail?._id,
      response: newResponse,
    };

    addNoteToTask(payload)
      .unwrap()
      .then(() => {
        toast.success("Response successfully added!");
        handleCancelResponse();
      })
      .catch(() => toast.error("Something went wrong!"));
  };

  return (
    <>
      <Box
        display="flex"
        alignItems="end"
        justifyContent="space-between"
        padding={3}
      >
        <Box display="inline-flex" alignItems="center" gap={2}>
          <Badge badgeContent={responses.length} color="primary">
            <FeedbackOutlined />
          </Badge>

          <Typography>Responses</Typography>
        </Box>

        {!showCreateResponse && (
          <Button
            startIcon={<Add />}
            variant="outlined"
            onClick={() => setShowCreateResponse(true)}
          >
            Response
          </Button>
        )}
      </Box>

      <Collapse in={showCreateResponse}>
        <Divider />

        <InnerShadowBox padding={3}>
          <TextField
            placeholder="Leave a response"
            fullWidth
            multiline
            rows={2}
            value={newResponse}
            onChange={(e) => setNewResponse(e.target.value)}
          />
        </InnerShadowBox>

        <Divider />

        <Box
          display="flex"
          justifyContent="flex-end"
          gap={2}
          paddingX={3}
          paddingY={2}
        >
          <Button disable={addingResponse} onClick={handleCancelResponse}>
            Cancel
          </Button>

          <Button
            variant="outlined"
            disabled={!newResponse.length || addingResponse}
            onClick={handleAddResponse}
          >
            Submit
          </Button>
        </Box>
      </Collapse>

      <Divider />

      <Box paddingX={3}>
        {!!responses?.length > 0 ? (
          responses.map((response, index) => (
            <React.Fragment key={response._id}>
              <Box display="flex" alignItems="start" gap={2} paddingY={3}>
                <FeedbackOutlined color="primary" style={{ marginTop: 3 }} />
                <Box display="flex" flexDirection="column" gap={1}>
                  <Box display="flex" alignItems="center" gap={2}>
                    <Typography> {response?.createdBy.name}</Typography>

                    <FiberManualRecord
                      style={{ color: "gray", fontSize: 10 }}
                    />

                    <Typography style={{ color: "gray" }}>
                      {localizeDateTimeMed(response?.createdAt)}
                    </Typography>
                  </Box>

                  <Box>{response?.content}</Box>
                </Box>
              </Box>
              {index !== responses.length - 1 && <Divider />}
            </React.Fragment>
          ))
        ) : (
          <EmptyResult height={150} />
        )}
      </Box>
    </>
  );
};

export default TaskResponses;
